import type { Schema } from '@dealdesk/api-schema';
import { z } from 'zod';

import { M } from '@/business';

type ProductCode = Schema<'ProductCode'>;
export const PRODUCT_CODES = ['PF', 'CC', 'HF', 'SME'] as const satisfies readonly ProductCode[];
/** Template select value for "start empty". Other values are `${bankId}:${versionNo}`. */
export const EMPTY_TEMPLATE = 'empty';

const code = z.string({ error: M.required }).trim().regex(/^[A-Z0-9_]{2,16}$/, 'validation.code');

const schemeRow = z.object({
  code,
  name: z.string({ error: M.required }).trim().min(1, M.required),
  nameAr: z.string(),
});

const productPart = {
  product: z.enum(PRODUCT_CODES, { error: M.required }),
  template: z.string().min(1, M.required),
  schemes: z.array(schemeRow).superRefine((rows, ctx) => {
    const seen = new Set<string>();
    rows.forEach((r, i) => {
      if (seen.has(r.code.trim())) ctx.addIssue({ code: 'custom', path: [i, 'code'], message: 'validation.server.duplicate' });
      seen.add(r.code.trim());
    });
  }),
};

const bankPart = {
  code,
  name: z.string({ error: M.required }).trim().min(2, M.required),
  nameAr: z.string(),
  agreementRef: z.string(),
  rmName: z.string(),
  submissionCutoff: z.string({ error: M.required }).regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'validation.time'),
};

/** New bank + first product. */
export const newBankSchema = z.object({ ...bankPart, ...productPart });
/** Another product for an existing bank: the bank fields are not shown, so they are not validated. */
export const addProductSchema = newBankSchema.extend({ code: z.string(), name: z.string(), submissionCutoff: z.string() });

export type OnboardingValues = z.infer<typeof newBankSchema>;
export type SchemeRowValues = z.infer<typeof schemeRow>;

export const onboardingDefaults = (product: ProductCode = 'PF'): OnboardingValues => ({
  code: '',
  name: '',
  nameAr: '',
  agreementRef: '',
  rmName: '',
  submissionCutoff: '14:00',
  product,
  template: EMPTY_TEMPLATE,
  schemes: [],
});
export const blankScheme: SchemeRowValues = { code: '', name: '', nameAr: '' };

const orNull = (v: string) => v.trim() || null;

export const toBankInput = (v: OnboardingValues): Schema<'BankInput'> => ({
  code: v.code.trim(),
  name: v.name.trim(),
  nameAr: orNull(v.nameAr),
  agreementRef: orNull(v.agreementRef),
  rmName: orNull(v.rmName),
  submissionCutoff: v.submissionCutoff,
});

export function toProductInput(v: Pick<OnboardingValues, 'product' | 'template'>): Schema<'BankProductInput'> {
  if (v.template === EMPTY_TEMPLATE) return { product: v.product, template: null };
  const [bankId = '', versionNo = ''] = v.template.split(':');
  return { product: v.product, template: { bankId, versionNo: Math.trunc(Number(versionNo)) } };
}

export const toSchemeInputs = (rows: readonly SchemeRowValues[]): Schema<'SchemeInput'>[] => rows.map((r) => ({ code: r.code.trim(), name: r.name.trim(), nameAr: orNull(r.nameAr) }));

/** Published versions of the product at other banks, usable as the starting template. */
export function templateOptions(banks: readonly Schema<'BankSummary'>[], product: ProductCode, exceptBankId: string | null) {
  return banks
    .filter((b) => b.id !== exceptBankId)
    .flatMap((b) => {
      const p = b.products.find((x) => x.product === product && x.publishedVersionNo !== null);
      return p?.publishedVersionNo ? [{ value: `${b.id}:${p.publishedVersionNo}`, bank: b.name, version: p.publishedVersionNo }] : [];
    });
}
